import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import styles from './UserProfileDashboard.module.css';

type Statistics = {
  totalProfiles?: number;
  profilesWithEmail?: number;
  profilesWithPhone?: number;
  profilesWithName?: number;
};

type Profile = {
  id: string;
  userId?: string;
  publicId?: string;
  name?: string;
  email?: string;
  phone?: string;
  pictureUrl?: string;
  facebookId?: string;
  linkedinId?: string;
};

type ProfileFormData = {
  userId: string;
  name: string;
  email: string;
  phone: string;
  pictureUrl: string;
  facebookId: string;
  linkedinId: string;
};

type SaveFilePickerOptions = {
  suggestedName?: string;
  types?: Array<{
    description?: string;
    accept: Record<string, string[]>;
  }>;
};

export default function UserProfileDashboard() {
  const { currentUser, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('list');
  const [formData, setFormData] = useState<ProfileFormData>({
    userId: '',
    name: '',
    email: '',
    phone: '',
    pictureUrl: '',
    facebookId: '',
    linkedinId: '',
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formMessage, setFormMessage] = useState('');
  const [listMessage, setListMessage] = useState('');
  const [listError, setListError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const escapeVCardValue = (value: unknown) => {
    if (value == null) return '';
    return String(value)
      .replace(/\\/g, '\\\\')
      .replace(/\n/g, '\\n')
      .replace(/\r/g, '')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,');
  };

  const buildVCardForProfile = (profile: Profile) => {
    const name = (profile?.name || '').trim();
    const email = (profile?.email || '').trim();
    const phone = (profile?.phone || '').trim();
    const publicProfileId = (profile?.userId || '').trim();

    const lines = ['BEGIN:VCARD', 'VERSION:3.0'];

    if (name) {
      lines.push(`FN:${escapeVCardValue(name)}`);
      lines.push(`N:${escapeVCardValue(name)};;;;`);
    } else if (email) {
      lines.push(`FN:${escapeVCardValue(email)}`);
    } else if (publicProfileId) {
      lines.push(`FN:${escapeVCardValue(publicProfileId)}`);
    }

    if (email) {
      lines.push(`EMAIL;TYPE=INTERNET:${escapeVCardValue(email)}`);
    }

    if (phone) {
      lines.push(`TEL;TYPE=CELL:${escapeVCardValue(phone)}`);
    }

    if (publicProfileId) {
      lines.push(`X-PUBLIC-PROFILE-ID:${escapeVCardValue(publicProfileId)}`);
    }

    lines.push('END:VCARD');
    return lines.join('\r\n');
  };

  const exportProfilesAsVcf = async () => {
    try {
      setListMessage('');
      setListError('');

      if (!profiles || profiles.length === 0) {
        setListError('No profiles to export.');
        return;
      }

      const vcfText = profiles.map(buildVCardForProfile).join('\r\n') + '\r\n';
      const blob = new Blob([vcfText], { type: 'text/vcard;charset=utf-8' });
      const suggestedName = `user-profiles-${new Date().toISOString().slice(0, 10)}.vcf`;

      const maybeShowSaveFilePicker = (window as unknown as { showSaveFilePicker?: unknown })
        .showSaveFilePicker;

      if (typeof maybeShowSaveFilePicker === 'function') {
        const fileHandle = await (
          maybeShowSaveFilePicker as (
            options?: SaveFilePickerOptions
          ) => Promise<FileSystemFileHandle>
        )({
          suggestedName,
          types: [
            {
              description: 'vCard',
              accept: {
                'text/vcard': ['.vcf'],
                'text/x-vcard': ['.vcf'],
                'text/plain': ['.vcf'],
              },
            },
          ],
        });

        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
        setListMessage('Exported VCF successfully.');
        return;
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = suggestedName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setListMessage('Export started.');
    } catch (e: unknown) {
      const err = e as { name?: string; message?: string };
      const message =
        err?.name === 'AbortError' ? 'Export cancelled.' : err?.message || 'Failed to export VCF.';
      setListError(message);
    }
  };

  const userProfileServiceUrl =
    import.meta.env.VITE_USER_PROFILE_SERVICE_URL === 'RELATIVE'
      ? ''
      : import.meta.env.VITE_USER_PROFILE_SERVICE_URL || '';

  const { data: statistics, isLoading: statsLoading, error: statsError } = useQuery<Statistics | null>({
    queryKey: ['userProfileStatistics', userProfileServiceUrl],
    enabled: Boolean(currentUser),
    queryFn: async () => {
      const response = await fetch(`${userProfileServiceUrl}/api/user-profiles/admin/statistics`, {
        headers: { Authorization: 'Basic ' + btoa('admin:admin123'), 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return response.json() as Promise<Statistics>;
    },
  });

  const { data: profiles = [], isLoading: profilesLoading, error: profilesError } = useQuery<Profile[]>({
    queryKey: ['userProfiles'],
    enabled: Boolean(currentUser),
    queryFn: async () => {
      const response = await fetch('/api/user-profiles', { headers: { 'Content-Type': 'application/json' } });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return response.json() as Promise<Profile[]>;
    },
  });

  const loading = statsLoading || profilesLoading;
  const error = statsError ? (statsError as Error).message : profilesError ? (profilesError as Error).message : null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormLoading(true);
    setFormMessage('');

    try {
      // Make userId optional by generating one if omitted
      const payload = {
        ...formData,
        userId:
          formData.userId && formData.userId.trim() !== ''
            ? formData.userId.trim()
            : `user-${Date.now()}`,
      };
      const response = await fetch(`${userProfileServiceUrl}/api/user-profiles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Basic ' + btoa('admin:admin123'),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      await response.json();
      queryClient.invalidateQueries({ queryKey: ['userProfiles'] });
      queryClient.invalidateQueries({ queryKey: ['userProfileStatistics'] });
      setFormData({
        userId: '',
        name: '',
        email: '',
        phone: '',
        pictureUrl: '',
        facebookId: '',
        linkedinId: '',
      });
      setFormMessage('Profile created successfully!');
    } catch (error: unknown) {
      setFormMessage(`Error: ${error instanceof Error ? error.message : 'Request failed.'}`);
    } finally {
      setFormLoading(false);
    }
  };

  const handleRefresh = () => {
    setListMessage('');
    setListError('');
    queryClient.invalidateQueries({ queryKey: ['userProfileStatistics'] });
    queryClient.invalidateQueries({ queryKey: ['userProfiles'] });
  };

  const handleDeleteProfile = async (profile: Profile) => {
    if (!profile?.id) return;

    const confirmed = window.confirm(
      `Delete the profile for ${profile.name || profile.email || profile.userId || 'this user'}? This cannot be undone.`
    );

    if (!confirmed) return;

    setDeletingId(profile.id);
    setListMessage('');
    setListError('');

    try {
      const response = await fetch(`/api/user-profiles/${profile.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 404) {
        throw new Error('Profile not found or already removed.');
      }

      if (!response.ok && response.status !== 204) {
        throw new Error(`Failed to delete profile (status ${response.status}).`);
      }

      queryClient.invalidateQueries({ queryKey: ['userProfiles'] });
      queryClient.invalidateQueries({ queryKey: ['userProfileStatistics'] });
      setListMessage('Profile deleted successfully.');
    } catch (deleteError: unknown) {
      console.error('Error deleting profile:', deleteError);
      setListError(
        deleteError instanceof Error ? deleteError.message : 'Failed to delete profile.'
      );
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.inner}>
          <div className={styles.card}>
            <div className={styles.section}>
              <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                  <div className={styles.statValue}>…</div>
                  <div className={styles.statLabel}>Loading</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.inner}>
          <div className={styles.card}>
            <div className={styles.section}>
              <h1 className={styles.formTitle}>Error Loading Dashboard</h1>
              <p className={styles.listError}>{error}</p>
              <button onClick={handleRefresh} className={styles.primaryButton}>
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        <div className={styles.card}>
          <header className={styles.header}>
            <div className={styles.headerRow}>
              <div>
                <h1 className={styles.title}>User Profile Dashboard</h1>
                <p className={styles.subtitle}>Create and manage user profiles</p>
              </div>
              <button onClick={handleRefresh} className={styles.refreshButton}>
                Refresh Data
              </button>
            </div>
          </header>

          <section className={styles.section}>
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{statistics?.totalProfiles || 0}</div>
                <div className={styles.statLabel}>Total Profiles</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{statistics?.profilesWithEmail || 0}</div>
                <div className={styles.statLabel}>With Email</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{statistics?.profilesWithPhone || 0}</div>
                <div className={styles.statLabel}>With Phone</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{statistics?.profilesWithName || 0}</div>
                <div className={styles.statLabel}>With Name</div>
              </div>
            </div>
          </section>

          <nav className={styles.tabs}>
            <button
              onClick={() => setActiveTab('create')}
              aria-selected={activeTab === 'create'}
              className={`${styles.tabButton} ${activeTab === 'create' ? styles.tabButtonActive : ''}`}
            >
              Create Profile
            </button>
            <button
              onClick={() => setActiveTab('list')}
              aria-selected={activeTab === 'list'}
              className={`${styles.tabButton} ${activeTab === 'list' ? styles.tabButtonActive : ''}`}
            >
              All Profiles ({profiles.length})
            </button>
          </nav>

          <div className={styles.content}>
            {activeTab === 'create' && (
              <div className={styles.formSection}>
                <h2 className={styles.formTitle}>Create New User Profile</h2>
                <form onSubmit={handleSubmit} className={styles.formGrid}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>User ID (optional)</label>
                    <input
                      type="text"
                      name="userId"
                      value={formData.userId}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder="unique-user-id"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder="John Doe"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder="john@example.com"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder="+1234567890"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Picture URL</label>
                    <input
                      type="url"
                      name="pictureUrl"
                      value={formData.pictureUrl}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder="https://example.com/photo.jpg"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Facebook ID</label>
                    <input
                      type="text"
                      name="facebookId"
                      value={formData.facebookId}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder="facebook-username"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>LinkedIn ID</label>
                    <input
                      type="text"
                      name="linkedinId"
                      value={formData.linkedinId}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder="linkedin-username"
                    />
                  </div>

                  <div className={styles.actionsRow}>
                    <button type="submit" disabled={formLoading} className={styles.primaryButton}>
                      {formLoading ? 'Creating…' : 'Create Profile'}
                    </button>
                    {formMessage && (
                      <div
                        className={`${styles.formMessage} ${formMessage.includes('Error') ? styles.formMessageError : styles.formMessageSuccess}`}
                      >
                        {formMessage}
                      </div>
                    )}
                  </div>
                </form>
              </div>
            )}

            {activeTab === 'list' && (
              <div>
                <div className={styles.listHeader}>
                  <h2 className={styles.formTitle}>All User Profiles</h2>
                  <div className={styles.toolBar}>
                    <button
                      type="button"
                      className={styles.toolButton}
                      onClick={exportProfilesAsVcf}
                      title="Export VCF"
                      aria-label="Export VCF"
                    >
                      <svg
                        className={styles.toolButtonIcon}
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    </button>
                  </div>
                </div>
                {listMessage && <div className={styles.listMessage}>{listMessage}</div>}
                {listError && <div className={styles.listError}>{listError}</div>}
                <div className={styles.tableWrapper}>
                  <div className={styles.tableScroll}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Public ID</th>
                          <th>Email</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {profiles.map(profile => (
                          <tr key={profile.id}>
                            <td className={styles.profileName}>{profile.name || '-'}</td>
                            <td className={styles.profileId}>
                              {profile.publicId ? (
                                <a
                                  href={`/profile/${encodeURIComponent(profile.publicId)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.profileId}
                                >
                                  {profile.publicId}
                                </a>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className={styles.profileEmail}>{profile.email || '-'}</td>
                            <td className={styles.actionCell}>
                              <a
                                href={`/user-profile-admin?id=${encodeURIComponent(profile.userId ?? '')}`}
                                className={styles.editButton}
                              >
                                Edit
                              </a>
                              <button
                                type="button"
                                onClick={() => handleDeleteProfile(profile)}
                                disabled={deletingId === profile.id}
                                className={styles.deleteButton}
                              >
                                {deletingId === profile.id ? 'Deleting…' : 'Delete'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {profiles.length === 0 && (
                    <div className={styles.emptyState}>
                      No profiles found. Create your first profile using the form above.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
